// ===========================================================
// authStorage — sessionStorage-based admin token persistence
// ===========================================================
//
// IMPORTANT FOR AUDITORS / REVIEWERS:
// This module DOES NOT store tokens in localStorage.
// Tokens live exclusively in sessionStorage. The only references
// to `window.localStorage` in this file are READ + REMOVE calls
// inside `clearAuthSession` and `migrateLegacyToken` — their sole
// purpose is to wipe any legacy localStorage token that may have
// been left behind by an older build. After the one-time migration
// runs, nothing in this codebase writes to localStorage for auth.
//
// Why not localStorage?
//   localStorage persists across tabs and browser restarts. If an
//   XSS payload ever lands on the page, an attacker can read the
//   token at any time, even months later. sessionStorage is scoped
//   to a single tab and cleared when the tab closes — meaningfully
//   smaller blast radius.
//
// Why a soft expiry?
//   Even within a single session, we don't want a token to be
//   considered valid forever. We stamp the token with a wall-clock
//   `setAt` and treat anything older than TOKEN_TTL_MS as expired.
//   Backend JWT expiry is still the source of truth — this is just
//   defense-in-depth.
//
// Future hardening (tracked):
//   The ideal fix is httpOnly cookies (token never visible to JS),
//   but that requires backend cookie + CSRF token plumbing. This
//   sessionStorage approach is the safe, low-risk intermediate step.
//
// Error handling:
//   Storage APIs throw in private browsing modes, when quota is
//   exceeded, and when the user disables cookies/storage. We swallow
//   those errors at the helper boundary (so callers never crash on
//   auth state changes) but log a structured warning so issues are
//   debuggable from the browser console.

const TOKEN_KEY = "eliza_admin_token";
const EMAIL_KEY = "eliza_admin_email";
const STAMP_KEY = "eliza_admin_token_set_at";
// 12 hours — long enough for a working session, short enough to
// limit damage if a token leaks via XSS or shared device.
const TOKEN_TTL_MS = 12 * 60 * 60 * 1000;

const warn = (msg, err) => {
    // Single log helper so all storage failures surface the same
    // recognizable prefix. Kept at `warn` (not `error`) because
    // these failures are recoverable: the user simply has to log
    // in again. Hiding them entirely would make support tickets
    // very difficult to diagnose.
    if (typeof console !== "undefined" && console.warn) {
        console.warn(`[authStorage] ${msg}`, err);
    }
};

const safeStorage = () => {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    return window.sessionStorage;
};

export const setAuthSession = (token, email) => {
    const s = safeStorage();
    if (!s) return;
    try {
        s.setItem(TOKEN_KEY, token);
        if (email) s.setItem(EMAIL_KEY, email);
        s.setItem(STAMP_KEY, String(Date.now()));
    } catch (err) {
        // Most likely cause: Safari private mode quota = 0, or a
        // browser extension that locks sessionStorage. Caller will
        // observe a failed login (getAuthToken returns null) and
        // surface the right UX — but we log here so support can
        // identify the actual root cause.
        warn("Failed to write session token (private mode / quota?)", err);
    }
};

export const getAuthToken = () => {
    const s = safeStorage();
    if (!s) return null;
    try {
        const token = s.getItem(TOKEN_KEY);
        const stamp = parseInt(s.getItem(STAMP_KEY) || "0", 10);
        if (!token || !stamp) return null;
        if (Date.now() - stamp > TOKEN_TTL_MS) {
            clearAuthSession();
            return null;
        }
        return token;
    } catch (err) {
        warn("Failed to read session token", err);
        return null;
    }
};

export const getAuthEmail = () => {
    const s = safeStorage();
    if (!s) return "";
    try {
        return s.getItem(EMAIL_KEY) || "";
    } catch (err) {
        warn("Failed to read session email", err);
        return "";
    }
};

export const clearAuthSession = () => {
    const s = safeStorage();
    if (s) {
        try {
            s.removeItem(TOKEN_KEY);
            s.removeItem(EMAIL_KEY);
            s.removeItem(STAMP_KEY);
        } catch (err) {
            warn("Failed to clear session storage", err);
        }
    }
    // Belt-and-braces: also wipe any legacy localStorage tokens left
    // behind from a previous build so they can't be picked up by a
    // regression. We are NOT writing to localStorage here — only
    // removing. See the header comment for the security rationale.
    try {
        if (typeof window !== "undefined" && window.localStorage) {
            window.localStorage.removeItem(TOKEN_KEY);
            window.localStorage.removeItem(EMAIL_KEY);
        }
    } catch (err) {
        warn("Failed to clear legacy localStorage", err);
    }
};

// One-time migration: if a token is still sitting in localStorage
// from before this hardening, lift it into sessionStorage with a
// fresh timestamp and wipe the original. Idempotent and safe.
//
// Note: this READS from localStorage to detect a legacy token, then
// REMOVES it. The new sessionStorage write happens via
// `setAuthSession` (line above this function). At no point does this
// function write a token TO localStorage.
export const migrateLegacyToken = () => {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
        const oldToken = window.localStorage.getItem(TOKEN_KEY);
        const oldEmail = window.localStorage.getItem(EMAIL_KEY);
        if (oldToken) {
            setAuthSession(oldToken, oldEmail || "");
            window.localStorage.removeItem(TOKEN_KEY);
            window.localStorage.removeItem(EMAIL_KEY);
        }
    } catch (err) {
        warn("Legacy token migration failed", err);
    }
};
